import React from 'react';
import { Layout, Flex, Input, Avatar, Badge, Typography } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ChannelSelect from '../ChannelSelect/ChannelSelect';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import SharedFilterBar from '../../../Shared/Filters/SharedFilterBar';
import { conversationFilterFields } from '../../../Shared/Filters/filterConfig';
import './ConversationsHeader.css';

const { Header } = Layout;

const ConversationsHeader = ({
    channels,
    loadingChannels,
    selectedChannel,
    onChannelChange,
    onChannelSearch,
    onSearch,
    loading,
    filters,
    onFiltersChange,
    sortValue,
    onSortChange,
}) => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const themeColors = {
        background: isDarkMode ? '#1f1f1f' : '#fff',
        border: isDarkMode ? '#434343' : '#f0f0f0',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        hoverBg: isDarkMode ? '#2a2a2a' : '#fafafa',
    };

    const currentChannel = channels?.find(c => c.id === selectedChannel);
    const agent = currentChannel?.agent;

    return (
        <Header style={{
            padding: '8px',
            height: 'auto',
            lineHeight: 'normal',
            background: themeColors.background,
            borderBottom: `1px solid ${themeColors.border}`,
            transition: 'background 0.3s ease, border-color 0.3s ease'
        }}>
            {/* Channel Select */}
            <ChannelSelect
                channels={channels}
                loading={loadingChannels}
                value={selectedChannel}
                onChange={onChannelChange}
                onSearch={onChannelSearch}
            />

            {selectedChannel && (
                <div style={{
                    marginTop: 4,
                    marginBottom: 4,
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 6,
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'}`,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {agent ? (
                            <Badge dot status={agent.is_started ? 'success' : 'default'} offset={[-2, 20]}>
                                <Avatar
                                    size={24}
                                    icon={<RobotOutlined />}
                                    style={{
                                        backgroundColor: agent.is_started
                                            ? (isDarkMode ? 'rgba(82, 196, 26, 0.2)' : '#f6ffed')
                                            : (isDarkMode ? 'rgba(24, 144, 255, 0.2)' : '#e6f7ff'),
                                        color: agent.is_started
                                            ? '#52c41a'
                                            : (isDarkMode ? '#177ddc' : '#1890ff'),
                                        border: 'none'
                                    }}
                                />
                            </Badge>
                        ) : (
                            <Avatar
                                size={24}
                                icon={<RobotOutlined />}
                                style={{
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#f5f5f5',
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : '#d9d9d9',
                                    border: 'none'
                                }}
                            />
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            {agent ? (
                                <Typography.Text strong style={{ fontSize: 12, lineHeight: 1.2, color: themeColors.text }}>
                                    {agent.name}
                                </Typography.Text>
                            ) : (
                                <Typography.Text style={{ fontSize: 12, color: themeColors.secondaryText }}>
                                    {t('no_agent_yet')}
                                </Typography.Text>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Search + Shared Filters + Reset */}
            <Flex
                wrap
                align="center"
                justify="space-between"
                style={{ marginTop: 10, gap: 8 }}
            >
                <Input.Search
                    placeholder={t('search_by_contact_name')}
                    style={{
                        flex: 1,
                        minWidth: 160,
                        backgroundColor: isDarkMode ? '#141414' : '#fff',
                        borderColor: themeColors.border,
                        color: themeColors.text
                    }}
                    onChange={onSearch}
                    loading={loading}
                    allowClear
                />

                <div className="conversation-filter-scroll">
                    <SharedFilterBar
                        fields={conversationFilterFields}
                        value={filters}
                        onChange={onFiltersChange}
                        sortFields={[
                            { value: 'updated_time', labelKey: 'filters_field_last_activity' },
                            { value: 'created_at', labelKey: 'created_at' },
                            { value: 'updated_at', labelKey: 'updated_at' },
                            { value: 'status', labelKey: 'filters_field_status' },
                            { value: 'contact_name', labelKey: 'full_name' },
                        ]}
                        sortValue={sortValue}
                        onSortChange={onSortChange}
                    />
                </div>
            </Flex>
        </Header>
    );
};

export default ConversationsHeader;
